import { CheckoutProducts } from '@app/components/checkout/CheckoutProducts/CheckoutProducts';
import { Button } from '@app/components/various/Button';
import { CancelledProductsTooltip } from '@app/components/various/CancelledProductsTooltip';
import { DelwinExpiredTooltip } from '@app/components/various/DelwinExpiredTooltip';
import { DelwinNameAndDate } from '@app/components/various/DelwinNameAndDate';
import { ChevronDown } from '@app/components/various/Icons';
import { getActiveDeliveryWindowsIdsForOrderBuyer, getAllDeliveryWindows, getOrderStatus } from '@app/ducks';
import { getDeliveryWindowsByIds, getIsExpired } from '@app/logic/deliveryWindows';
import { getIsOrderEditable, getUnitsCountInDelwin, hasCancelledProducts } from '@app/logic/Orders';
import { cn } from '@app/utils/cn';
import { useExpand } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { Order, Product } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SplitRow } from '../SplitRow';

interface Props {
  order: Order.Open;
  products: never[] | Record<Product.Id, Product.Full>;
  orderIndex?: number;
}

export const CheckoutOrderOverview = ({ order, products, orderIndex }: Props) => {
  const allDeliveryWindows = useSelector(getAllDeliveryWindows);
  const activeDeliveryWindowsIds = useSelector(getActiveDeliveryWindowsIdsForOrderBuyer);
  const { t } = useTranslation(['common', 'orders', 'totals']);
  const expanderId = React.useId();
  const orderStatus = useSelector(getOrderStatus);
  const isOrderEditable = getIsOrderEditable(orderStatus);

  const { isOpen, toggle, wrapperRef } = useExpand<HTMLDivElement>({
    initialOpen: false,
  });

  const deliveryWindows = getDeliveryWindowsByIds(allDeliveryWindows, order.deliveryWindows).filter(
    ({ deliveryWindow }) => getUnitsCountInDelwin(deliveryWindow, order.products) > 0,
  );
  const unitsInOrder = order.totals.itemsCount;

  if (unitsInOrder === 0) {
    return null;
  }

  const deliveryWindowIds = deliveryWindows.map(delwin => delwin.deliveryWindow);
  const totalPrice = order.totals.itemsTotalPrice;

  return (
    <div>
      <SplitRow isOpen={isOpen}>
        <div className="col-span-full flex items-center gap-1 sm:col-auto">
          <div>
            <p className="flex items-center gap-0.5 body-1">
              {isDefined(orderIndex) ? t('orders:order_index', { index: orderIndex }) : t('orders:order_one')}
              {hasCancelledProducts(order.products) && isOrderEditable && <CancelledProductsTooltip className="z-interactiveTableItem" />}
            </p>
            <div className="relative z-interactiveTableItem -ml-1 flex flex-wrap text-neutral-40">
              {deliveryWindows.map(deliveryWindow => (
                <DelwinNameAndDate key={deliveryWindow.deliveryWindow} deliveryWindow={deliveryWindow} insertCommas>
                  <span className="flex items-center gap-1">
                    {getIsExpired(deliveryWindow, activeDeliveryWindowsIds) && isOrderEditable && <DelwinExpiredTooltip />}
                    <span className="body-4">{deliveryWindow.name}</span>
                  </span>
                </DelwinNameAndDate>
              ))}
            </div>
          </div>
        </div>

        <p className="flex gap-2 body-2">
          <span className="text-neutral-40 sm:hidden">{t('common:unit_other')}</span>
          {unitsInOrder}
        </p>

        <p className="col-span-3 flex justify-end gap-2 body-2 sm:col-span-1">
          <span className="text-right text-neutral-40 sm:hidden">{t('totals:total_one')}</span>
          {totalPrice}
        </p>
        <div className="flex justify-center">
          <Button
            type="button"
            onClick={toggle}
            className="absolute right-3 top-3 size-5 shrink-0 overflow-visible absolute-cursor-pointer after:-inset-2 sm:static"
            aria-label={t('common:expand_row')}
            aria-expanded={isOpen}
            aria-controls={expanderId}
            icon={ChevronDown}
            variant="ghost"
            color="secondary"
            iconClassName={cx('m-2 motion-safe:transition-transform', { 'rotate-180': isOpen })}
          />
        </div>
      </SplitRow>
      <div
        id={expanderId}
        ref={wrapperRef}
        className={cn('hidden max-h-[60rem] overflow-scroll rounded-b-regular bg-white md:max-h-[49rem]', { block: isOpen })}
      >
        <CheckoutProducts variants={order.products} order={order} products={products} deliveryWindowIds={deliveryWindowIds} />
      </div>
    </div>
  );
};
